import React, { useEffect, useState } from 'react'
import { ExternalLink, Github, TrendingUp, ChevronLeft, ChevronRight, X, Code2 } from 'lucide-react'
import * as Si from 'react-icons/si'
import { useInView } from 'react-intersection-observer'
import Card from './Card'

const normalizeTech = (s) => s?.toLowerCase().replace(/[^a-z0-9]/g, '')
const iconNameByTech = {
  react: 'SiReact',
  reactnative: 'SiReact',
  nextjs: 'SiNextdotjs',
  nodejs: 'SiNodedotjs',
  html: 'SiHtml5',
  css: 'SiCss3',
  javascript: 'SiJavascript',
  bootstrap: 'SiBootstrap',
  php: 'SiPhp',
  mysql: 'SiMysql',
  postgresql: 'SiPostgresql',
  redis: 'SiRedis',
  tensorflow: 'SiTensorflow',
  tensorflowjs: 'SiTensorflow',
  stripe: 'SiStripe',
  aws: 'SiAmazonaws',
  typescript: 'SiTypescript',
  mongodb: 'SiMongodb',
  webrtc: 'SiWebrtc',
  socketio: 'SiSocketdotio',
  docker: 'SiDocker',
  kubernetes: 'SiKubernetes',
  tailwind: 'SiTailwindcss',
  tailwindcss: 'SiTailwindcss',
  firebase: 'SiFirebase',
  openai: 'SiOpenai',
  openaiapi: 'SiOpenai',
  coingecko: 'SiCoingecko',
  coingeckoapi: 'SiCoingecko',
  chartjs: 'SiChartdotjs',
  vuejs: 'SiVuedotjs',
  d3js: 'SiD3dotjs',
  express: 'SiExpress',
  pwa: 'SiPwa',
  openweather: 'SiOpenweather',
  openweatherapi: 'SiOpenweather',
  responsiveuiux: 'SiFigma',
  seo: 'SiGoogleanalytics',
  seooptimization: 'SiGoogleanalytics',
  figma: 'SiFigma',
  uiuxdesign: 'SiFigma',
  javascriptes6: 'SiJavascript',
  datavisualization: 'SiChartdotjs',
}
const TechIcon = ({ name, className }) => {
  const key = normalizeTech(name)
  const exportName = iconNameByTech[key]
  const Icon = exportName ? Si[exportName] : null
  return Icon ? <Icon className={className} /> : <Code2 className={className} />
}

const ProjectCard = ({ project, featured = false, reverse = false, delay = 0 }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  })
  const images = Array.isArray(project.images) && project.images.length ? project.images : [project.image]
  const [current, setCurrent] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const next = () => setCurrent((i) => (i + 1) % images.length)
  const prev = () => setCurrent((i) => (i - 1 + images.length) % images.length)
  useEffect(() => {
    if (lightboxOpen || images.length <= 1) return
    const id = setInterval(next, 4000)
    return () => clearInterval(id)
  }, [lightboxOpen, images.length])
  useEffect(() => {
    if (!lightboxOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false)
      if (e.key === 'ArrowRight') next()
      if (e.key === 'ArrowLeft') prev()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightboxOpen, images.length])
  if (featured) {
    return (
      <div
        ref={ref}
        className={`transition-all duration-1000 ${
          inView ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-20 scale-95'
        }`}
        style={{ transitionDelay: `${delay}ms` }}
      >
        <div
          className={`grid md:grid-cols-2 gap-8 items-center ${
            reverse ? 'md:grid-flow-dense' : ''
          }`}
        >
          {/* Image */}
          <div className={reverse ? 'md:col-start-2' : ''}>
            <div className="relative group overflow-hidden rounded-xl shadow-2xl">
              <img
                src={images[current]}
                alt={project.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 cursor-pointer"
                loading="lazy"
                onClick={() => setLightboxOpen(true)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prev}
                    aria-label="Previous image"
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 text-white p-2 rounded-full hover:bg-black/60"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    type="button"
                    onClick={next}
                    aria-label="Next image"
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 text-white p-2 rounded-full hover:bg-black/60"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Content */}
          <div className={reverse ? 'md:col-start-1 md:row-start-1' : ''}>
            <Card className="p-8">
              <div className="mb-4">
                <span className="inline-block px-3 py-1 text-xs font-semibold text-navy-medium bg-navy-medium/10 rounded-full">
                  {project.role}
                </span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-bold text-navy-dark dark:text-navy-lightest mb-4">
                {project.title}
              </h3>

              <p className="text-navy-base dark:text-navy-soft mb-6 leading-relaxed">
                {project.description}
              </p>

              {/* Challenge & Solution */}
              {project.challenge && (
                <div className="mb-6 p-4 bg-navy-pale dark:bg-navy-base rounded-lg">
                  <h4 className="font-semibold text-navy-dark dark:text-navy-lightest mb-2">
                    Challenge & Solution:
                  </h4>
                  <p className="text-sm text-navy-base dark:text-navy-soft">
                    <strong>Challenge:</strong> {project.challenge}
                  </p>
                  <p className="text-sm text-navy-base dark:text-navy-soft mt-2">
                    <strong>Solution:</strong> {project.solution}
                  </p>
                </div>
              )}

              {/* Tech Stack */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-navy-base dark:text-navy-soft mb-3">
                  Tech Stack
                </h4>
                <div className="flex flex-wrap gap-2">
                  {project.techStack.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 text-sm bg-white dark:bg-navy-base text-navy-base dark:text-navy-soft rounded-md border border-navy-soft dark:border-navy-medium flex items-center gap-2"
                    >
                      <TechIcon name={tech} className="w-4 h-4" />
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Metrics */}
              {project.metrics && (
                <div className="mb-6 flex items-center gap-2 text-mint dark:text-mint">
                  <TrendingUp size={18} />
                  <span className="text-sm font-semibold">{project.metrics}</span>
                </div>
              )}

              {/* Links */}
              <div className="flex gap-4">
                {project.liveUrl && (
                  <a
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-navy-medium hover:text-accent font-semibold transition-colors"
                  >
                    <ExternalLink size={18} />
                    Live Demo
                  </a>
                )}
                {project.githubUrl && (
                  <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-navy-base dark:text-navy-soft hover:text-accent font-semibold transition-colors"
                  >
                    <Github size={18} />
                    Source Code
                  </a>
                )}
              </div>
            </Card>
          </div>
        </div>
        {lightboxOpen && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center">
            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              aria-label="Close"
              className="absolute top-6 right-6 bg-white/20 text-white p-2 rounded-full hover:bg-white/30"
            >
              <X size={20} />
            </button>
            {images.length > 1 && (
              <button
                type="button"
                onClick={prev}
                aria-label="Previous"
                className="absolute left-6 top-1/2 -translate-y-1/2 bg-white/20 text-white p-3 rounded-full hover:bg-white/30"
              >
                <ChevronLeft size={24} />
              </button>
            )}
            <img
              src={images[current]}
              alt={project.title}
              className="max-w-[90vw] max-h-[80vh] object-contain rounded-lg"
            />
            {images.length > 1 && (
              <button
                type="button"
                onClick={next}
                aria-label="Next"
                className="absolute right-6 top-1/2 -translate-y-1/2 bg-white/20 text-white p-3 rounded-full hover:bg-white/30"
              >
                <ChevronRight size={24} />
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  // Simple card for other projects
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        inView ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-20 scale-95'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <Card className="p-6 h-full flex flex-col hover:shadow-xl hover:scale-105 hover:-translate-y-2 transition-all duration-300">
        <div className="mb-4">
          <span className="inline-block px-3 py-1 text-xs font-semibold text-navy-medium bg-navy-medium/10 rounded-full">
            {project.category || 'Side Project'}
          </span>
        </div>

        <h3 className="text-xl font-bold text-navy-dark dark:text-navy-lightest mb-3">
          {project.title}
        </h3>

        <p className="text-navy-base dark:text-navy-soft mb-4 flex-grow">
          {project.description}
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {project.techStack.slice(0, 3).map((tech) => (
            <span
              key={tech}
              className="px-2 py-1 text-xs bg-navy-pale dark:bg-navy-base text-navy-base dark:text-navy-soft rounded flex items-center gap-1.5"
            >
              <TechIcon name={tech} className="w-3.5 h-3.5" />
              {tech}
            </span>
          ))}
        </div>

        <div className="flex gap-4 mt-auto">
          {project.liveUrl && (
            <a
              href={project.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-navy-medium hover:text-accent transition-colors"
              aria-label="Live demo"
            >
              <ExternalLink size={20} />
            </a>
          )}
          {project.githubUrl && (
            <a
              href={project.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-navy-base dark:text-navy-soft hover:text-accent transition-colors"
              aria-label="GitHub repository"
            >
              <Github size={20} />
            </a>
          )}
        </div>
      </Card>
    </div>
  )
}

export default ProjectCard